import styled from '@emotion/styled';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { MusicPlayer } from 'src/modules/meditations/components/MusicPlayer';
import { StepButtons, StepButtonsProps } from 'src/modules/programs/components/StepButtons';
import * as S from 'src/modules/programs/stepComponents/styles';
import congratulationsIcon from 'src/public/img/congratulations.svg';
import stepIcon from 'src/public/img/icon-support-type-live.svg';
import { Modal } from 'src/shared/components/Modal';
import { formatStringsArrayWithAnd } from 'src/shared/helpers/formatStringsArrayWithAnd';
import { color, font, layout } from 'src/shared/styles/variables';

import { setMindfulnessExerciseCompleted } from '../actions/weekOneActions';
import {
    getMindfulnessImprovementsDescriptions,
    isMindfulnessExerciseCompleted,
} from '../selectors/weekOneSelectors';

const StepContent = styled('div')({
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    flexGrow: 1,
    justifyContent: 'space-between',
});

const StyledModal = styled(Modal)({
    '& .ant-modal-content': {
        maxWidth: layout.modalWidth.wide,
        margin: '0 auto',

        '& .ant-modal-body': {
            display: 'flex',
            flexDirection: 'column',
            padding: '4rem',
        },
        '& .ant-modal-footer': {
            padding: 0,
        },
    },
});

const ModalIcon = styled('i')({
    display: 'block',
    backgroundImage: `url('${congratulationsIcon}')`,
    height: '12rem',
    width: '12rem',
    margin: '2rem auto',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
});

const ModalTitle = styled('h3')(
    {
        width: '100%',
        margin: '0 0 2rem',
        color: color.chathamsBlue,
        textAlign: 'center',
    },
    font.heavy(font.size.header),
);

const ModalText = styled('p')(
    {
        color: color.scorpion,
    },
    font.book(font.size.large),
);

const MusicPlayerWrapper = styled.div({
    width: '100%',
    bottom: 0,
});

interface ActionsProps {
    setMindfulnessExerciseCompleted: typeof setMindfulnessExerciseCompleted;
}

interface StateProps {
    mindfulnessImprovementsDescriptions: string[];
    isExerciseCompleted: boolean;
}

type Props = StepButtonsProps & StateProps & ActionsProps & WithTranslation;

interface State {
    isCongratulationsModalOpen: boolean;
}

export class MindfulnessExerciseStepContainer extends React.Component<Props> {
    public state: State = {
        isCongratulationsModalOpen: false,
    };

    private handleMeditationListened = () => {
        this.props.setMindfulnessExerciseCompleted();
    };

    private handleMeditationFinished = (): void => {
        this.props.setMindfulnessExerciseCompleted();
        this.setState({
            isCongratulationsModalOpen: true,
        });
    };

    private handleCloseModal = (): void => this.setState({ isCongratulationsModalOpen: false });

    public render(): JSX.Element {
        const {
            goToNextStep,
            goToPreviousStep,
            isExerciseCompleted,
            mindfulnessImprovementsDescriptions,
            t,
        } = this.props;

        const descriptions: string[] = t('week-1.mindfulness-exercise.descriptions', {
            returnObjects: true,
        });

        return (
            <>
                <StepContent>
                    <S.ContentRow>
                        <S.ContentColumn md={14}>
                            <S.StepHeading small>
                                {t('week-1.mindfulness-exercise.smallHeading')}
                            </S.StepHeading>
                            <S.StepHeading>
                                {t('week-1.mindfulness-exercise.heading')}
                            </S.StepHeading>
                            {descriptions.map((description: string) => (
                                <S.StepDescription key={description}>
                                    {description}
                                </S.StepDescription>
                            ))}
                        </S.ContentColumn>
                        <S.ContentColumn md={10}>
                            <S.StepIcon iconUrl={stepIcon} />
                        </S.ContentColumn>
                    </S.ContentRow>

                    <MusicPlayerWrapper>
                        <MusicPlayer
                            track="https://assets.ctfassets.net/a9kub91sykpy/6suugKKRMWQwkCECKIIQU8/a9c99949a06ef07762965369ef6ea7aa/Meditation_for_Beginners_-_3_minute.mp3" // TODO: move to s3
                            color={color.scooter}
                            onListened={this.handleMeditationListened}
                            onTrackEnd={this.handleMeditationFinished}
                            centered
                        />
                    </MusicPlayerWrapper>
                </StepContent>
                <StepButtons
                    onPreviousClick={goToPreviousStep}
                    onNextClick={goToNextStep}
                    nextDisabled={!isExerciseCompleted}
                    hasNextStep
                />
                <StyledModal
                    visible={this.state.isCongratulationsModalOpen}
                    onCancel={this.handleCloseModal}
                    width="auto"
                    closable={false}
                    centered
                    footer={
                        <StepButtons
                            onPreviousClick={goToPreviousStep}
                            onNextClick={goToNextStep}
                            hasNextStep
                        />
                    }
                >
                    <ModalIcon />
                    <ModalTitle>{t('week-1.mindfulness-exercise.modal.title')}</ModalTitle>
                    <ModalText>
                        {t('week-1.mindfulness-exercise.modal.content')}
                        {mindfulnessImprovementsDescriptions &&
                            ` You are on your way to
                            ${formatStringsArrayWithAnd(mindfulnessImprovementsDescriptions)}.`}
                    </ModalText>
                </StyledModal>
            </>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    mindfulnessImprovementsDescriptions: getMindfulnessImprovementsDescriptions(state),
    isExerciseCompleted: isMindfulnessExerciseCompleted(state),
});

export const MindfulnessExerciseStep = withTranslation('cp')(
    connect(mapStateToProps, {
        setMindfulnessExerciseCompleted,
    })(MindfulnessExerciseStepContainer),
);
